import { MouseEvent, useRef, useState } from 'react'
import { Grid, Typography, Box, Button } from '@mui/material'
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import LinkIcon from '@mui/icons-material/Link'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useReactToPrint } from 'react-to-print'
import { useTranslation } from 'react-i18next'
import html2canvas from 'html2canvas'

import { HelperWrapper, QRCode } from 'src/components'
import { useCopy, useProductOverrideUrl } from 'src/hooks'
import { SPACES } from 'src/constants'
import { createProductQRLink } from 'src/common/utils'
import { UpdateProductQrLink } from './update-product-qr-link.component'
import { ProductCodeUrls } from '../qr-code-paper/product-code-urls.component'
import { LocaleSelect } from './locale-select.component'
import { ShareUploadIcon } from 'src/assets/icons'

import useStyles from './styles'
import { ICodeCardButtons } from 'src/types'

const codeCardDefaultButtonsSettings = {
  download: true,
  view: true,
  copy: true,
  print: true,
  email: true,
  share: true,
}

interface ICodeCardProps {
  buttonsSettings?: ICodeCardButtons
  title?: string
  disableBorder?: boolean
  productId?: string
  simpleProductId?: string
  satellite_map?: boolean
  outerLng?: string
  isDisabled?: boolean
  hideTitle?: boolean
  qrFullLink?: string
}

const CodeCard = ({
  buttonsSettings = codeCardDefaultButtonsSettings,
  title,
  disableBorder,
  productId,
  simpleProductId,
  outerLng,
  isDisabled,
  hideTitle = false,
  qrFullLink,
}: ICodeCardProps) => {
  const [, i18n] = useTranslation()
  const [lng, setLng] = useState(i18n.language)
  const { data: urlData } = useProductOverrideUrl(outerLng || lng, productId)

  const classes = useStyles()
  const qrRef = useRef<HTMLDivElement>(null)

  const handlePrintClick = useReactToPrint({ content: () => qrRef.current })

  const handleEmailClick = (e: MouseEvent) => {
    e.stopPropagation()

    const emailReceiver = process.env.REACT_APP_CONTACT_EMAIL

    window.location.href = `mailto:${emailReceiver}`
  }

  const handleDownloadClick = async () => {
    if (!qrRef.current) return

    const element = qrRef.current
    const canvas = await html2canvas(element)

    const data = canvas.toDataURL('image/jpg')
    const link = document.createElement('a')

    link.href = data
    link.download = 'image.jpg'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleShareClick = (e: MouseEvent) => {
    e.stopPropagation()

    navigator.share && navigator.share({ url: qrFullLink ? qrFullLink : window.location.href })
  }

  const handleLinkClick = (e: MouseEvent) => {
    e.stopPropagation()
  }

  const handleLng = (lng: string) => setLng(lng)

  const qrLink = simpleProductId
    ? createProductQRLink({ productId: simpleProductId, lng: outerLng || lng })
    : qrFullLink
      ? qrFullLink
      : undefined
  const handleCopy = useCopy(qrLink)

  return (
    <Box
      className={`${classes.cardWrapper} ${!disableBorder ? classes.cardWrapperBorder : ''}`}
      sx={{ padding: qrFullLink ? '6px' : '12px' }}
    >
      {!hideTitle && (
        <div className={classes.titleWrapper}>
          <Typography className={classes.cardTitle}>
            {title || <HelperWrapper t="qr-card-section-title" verticalPosition={10} size={11} />}
          </Typography>
          {productId && !outerLng && <LocaleSelect locale={lng} handleLocale={handleLng} />}
        </div>
      )}
      <div>
        <div className={classes.contentWrapper}>
          <Grid
            ref={qrRef}
            display={'flex'}
            p={qrFullLink ? '6px 12px 6px 6px' : SPACES.s}
            justifyContent={'flex-end'}
            className={classes.qrWrapper}
            item
          >
            <QRCode qrLink={qrLink} />
          </Grid>
          <Grid
            container
            className={`${classes.buttonsWrapper} ${qrFullLink ? classes.buttonsColumn : ''}`}
          >
            {buttonsSettings.view && (
              <Grid item>
                <Button
                  onClick={handleLinkClick}
                  component="a"
                  disabled={!qrLink}
                  className={`${classes.cardButton} ${!qrLink ? classes.cardButtonDisabled : ''}`}
                  href={qrLink}
                  target="_blank"
                  startIcon={<LinkIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="view-button-text" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {buttonsSettings.download && (
              <Grid item>
                <Button
                  className={classes.cardButton}
                  disabled={!qrLink}
                  onClick={handleDownloadClick}
                  startIcon={<DownloadIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="download-button-text" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {buttonsSettings.copy && (
              <Grid item>
                <Button
                  className={classes.cardButton}
                  disabled={!qrLink}
                  onClick={handleCopy}
                  startIcon={<ContentCopyIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="copy-button-text" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {buttonsSettings.print && (
              <Grid item>
                <Button
                  onClick={handlePrintClick}
                  className={classes.cardButton}
                  startIcon={<LocalPrintshopOutlinedIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="print-button-text" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {buttonsSettings.email && (
              <Grid item>
                <Button
                  onClick={handleEmailClick}
                  className={classes.cardButton}
                  startIcon={<MailOutlinedIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="email-input-label" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {buttonsSettings.share && typeof navigator.share === 'function' && (
              <Grid item>
                <Button
                  className={classes.cardButton}
                  onClick={handleShareClick}
                  startIcon={<ShareUploadIcon className={classes.buttonIcon} />}
                >
                  <Typography className={classes.buttonText}>
                    <HelperWrapper t="share-button-text" verticalPosition={12} />
                  </Typography>
                </Button>
              </Grid>
            )}
            {productId && !isDisabled && (
              <UpdateProductQrLink
                url={urlData?.override_url || null}
                productId={productId}
                lngCode={outerLng || lng}
              />
            )}
          </Grid>
        </div>
        {productId && (
          <div>
            <ProductCodeUrls overrideUrl={urlData?.override_url || null} qrLink={qrLink || null} />
            {/* {typeof satellite_map !== 'undefined' && (
              <>
                <Grid className={classes.overrideWrapper}>
                  <Typography className={classes.inputType}>
                    <HelperWrapper
                      t="qr-code-options-title"
                      ns={ETranslationNs.Common}
                      size={11}
                      verticalPosition={10}
                    />
                  </Typography>
                  <SatelliteViewCheckbox checked={satellite_map} isDisabled={isDisabled} />
                </Grid>
              </>
            )} */}
          </div>
        )}
      </div>
    </Box>
  )
}

export { CodeCard }
